import React from 'react';
import { Link } from 'react-router-dom';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        className="py-14 px-6 text-white"
        style={{
          background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 40%, #F58A54 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-white/80 text-sm">Last Updated: 3/12/2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          {/* Intro */}
          <p className="text-gray-700 leading-relaxed mb-8">
            These Terms of Service ("Terms") govern your access to and use of the Stackd platform operated by Atlas Procurement Solution, LLC DBA Stackd ("Stackd," "we," "our," or "us"). These Terms apply to all users of the platform including individuals seeking products, services, financing, or quotes ("Consumers") and businesses that provide quotes, products, services, or advertising on the platform ("Vendors").
          </p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Eligibility and Accounts</h2>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old to use Stackd. Users may create accounts to access certain features. Vendors must provide accurate business information and maintain required licenses and regulatory compliance applicable to their services.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. US-Only Availability</h2>
            <p className="text-gray-700 leading-relaxed">
              The Platform is intended for use only by residents of the United States and the District of Columbia. We may limit or refuse access if you are outside these locations.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Platform Description</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd provides tools that allow consumers to research purchases, request quotes, compare vendors, discover financing, and access third‑party offers. Stackd may also allow vendors to respond to quote requests, advertise services, or provide purchase options. Stackd does not:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Sell products or services directly</li>
              <li>Act as an agent, broker, or representative of vendors, retailers, lenders, or insurers</li>
              <li>Provide financial, investment, legal, tax, or medical advice</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              Recommendations are intended to support users in making a more informed decision only.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Any information on the Platform is for general informational and educational purposes only. You are responsible for verifying information and making your own decisions. Consult qualified professionals as needed.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Third‑Party Transactions, Links, and Transactions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd does not manufacture, sell, or directly provide any products or services displayed on the platform. Transactions occur between consumers and third‑party providers. Stackd is not responsible for the quality, delivery, pricing, warranties, or fulfillment of third‑party products or services.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Platform may show offers, listings, reviews, quotes, and links to third-party websites and providers ("Third-Party Providers"). When you click a link, you may leave the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              All purchases, applications, bookings, services, and transactions occur with Third-Party Providers and are governed by their terms and policies (including returns, cancellations, warranties, shipping, fulfillment, and disputes). We do not control and are not responsible for Third-Party Providers, their content, or their performance.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Vendor Participation</h2>
            <p className="text-gray-700 leading-relaxed">
              Businesses using Stackd to provide quotes or services must comply with all applicable laws and maintain required licenses, insurance, and permits. Vendors must provide accurate pricing and service descriptions and may only contact consumers regarding legitimate quote requests.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Vendor Fees and Advertising</h2>
            <p className="text-gray-700 leading-relaxed">
              Stackd may charge vendors fees for advertising, lead generation, quote participation, subscriptions, or other promotional placements. Fee structures may vary and will be disclosed through vendor agreements or onboarding materials.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. Reviews and Reputation</h2>
            <p className="text-gray-700 leading-relaxed">
              Consumers may leave reviews regarding vendors. Vendors may not manipulate, solicit fraudulent reviews, or provide incentives in exchange for reviews. Stackd reserves the right to moderate or remove reviews.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">8. Affiliate Relationships and Referral Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd may earn commissions or referral fees when users click links or purchase products through partner merchants. Referral tracking may depend on cookies or tracking technologies and may not always function correctly depending on user settings.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Platform may allow you to submit content, such as vendor reviews, ratings, comments, notes, project details, or support requests ("User Content"). You retain ownership of your User Content.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You grant Stackd a non-exclusive, worldwide, royalty-free, sublicensable, and transferable license to host, store, reproduce, modify (for formatting and display), distribute, display, and use your User Content to operate, improve, and promote the Platform, including to display vendor reviews to other users.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">9. Review Standards</h2>
            <p className="text-gray-700 leading-relaxed mb-4">If you submit vendor reviews, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 pl-2">
              <li>Be truthful, accurate, and based on your experience</li>
              <li>Do not post confidential information or personal data of others</li>
              <li>Do not post unlawful, harassing, defamatory, hateful, or obscene content</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We may remove or refuse to display reviews in our discretion.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">10. Acceptable Use and Restrictions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Users may not misuse the platform, attempt to access restricted areas, scrape or reverse engineer services, interfere with platform functionality, or submit false or misleading information.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2">
              <li>Violate any law or regulation</li>
              <li>Misrepresent your identity or submit false information</li>
              <li>Attempt to access non-public areas or bypass security</li>
              <li>Interfere with the Platform (including introducing malware or harmful code)</li>
              <li>Use bots, scrapers, crawlers, spiders, or automated means to access the Platform without our written permission</li>
              <li>Reverse engineer, decompile, or attempt to derive source code (except where legally prohibited)</li>
              <li>Sell, sublicense, rent, or commercially exploit the Platform without permission</li>
              <li>Engage in deceptive practices intended to manipulate referral tracking or derive improper financial benefit</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">11. User/Vendor Feedback</h2>
            <p className="text-gray-700 leading-relaxed">
              If you send/share feedback, ideas, or suggestions with the Stackd team ("Feedback"), you agree we may use them without restriction or compensation.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">12. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              The Platform and all content and materials (excluding User Content), including software, designs, text, graphics, logos, and trademarks, are owned by Stackd or its licensors and are protected by intellectual property laws. Except as expressly permitted, you may not copy, modify, distribute, or create derivative works from the Platform.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">13. Communications</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By creating an account or using the Platform, you consent to receive communications from us electronically (e.g., email, in-app messages, and push notifications where enabled) related to your account, security, transactions, and Platform updates. Marketing communications, if any, will provide opt-out options as required by law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you provide a phone number, we may contact you for account security, verification, or support. Standard message and data rates may apply.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">14. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              Stackd may suspend or terminate any account, including vendor accounts, for violations of these Terms, suspected fraud, misuse of the platform, or risk to users.
            </p>
          </section>

          {/* Section 15 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">15. Disclaimers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The platform and all information are provided 'as‑is' without warranties of any kind. Stackd does not guarantee vendor performance, warranties, express or implied, fitness for a particular purpose, product availability, pricing accuracy, platform uptime, or non-infringement. We do not warrant that the platform will be uninterrupted, error-free, secure, or that the information on the platform will at all times be accurate, complete, or current.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">15.1 AI-Generated Prices and Availability</h3>
              <p className="text-gray-700 leading-relaxed">
                Certain product and service options displayed on the Platform — including prices, availability, and links — are generated by artificial intelligence and sourced from web search results. These AI-estimated prices and options may not reflect current availability, actual booking prices, or live inventory. Links provided may lead to search results pages or general listing pages rather than the exact product or package described. Stackd makes no representation that any option, price, or availability shown is accurate, current, or bookable at the time of viewing. <strong>Users should always verify pricing, availability, and details directly on the vendor's or booking platform's website before making any purchase or reservation.</strong> Stackd is not responsible for discrepancies between AI-generated estimates and actual prices or availability offered by third-party vendors.
              </p>
            </div>
          </section>

          {/* Section 16 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">16. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed font-medium uppercase text-sm tracking-wide bg-gray-50 border border-gray-200 rounded-lg p-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, STACKD WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM OR RELATED TO YOUR USE OF THE PLATFORM OR THIRD-PARTY PROVIDERS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, STACKD FINANCIAL'S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATED TO THESE TERMS OR THE PLATFORM WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS (US $100) OR THE AMOUNT YOU PAID TO STACKD FINANCIAL IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, WHICHEVER IS GREATER.
            </p>
          </section>

          {/* Section 17 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">17. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless Stackd and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Platform, your User Content, or your violation of these Terms.
            </p>
          </section>

          {/* Section 18 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">18. Dispute Resolution and Arbitration Agreement (Class Action Waiver)</h2>
            <p className="text-gray-700 leading-relaxed font-semibold mb-4 uppercase text-sm tracking-wide bg-gray-50 border border-gray-200 rounded-lg p-3">
              PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR RIGHTS.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">18.1 Informal Resolution First</h3>
                <p className="text-gray-700 leading-relaxed">
                  Before filing a claim, you agree to try to resolve disputes informally by sending written notice to us describing the issue and your requested relief at: [Insert Legal Notice Email / Address]. We will attempt to resolve the dispute within sixty (60) days.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">18.2 Binding Arbitration</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  If we cannot resolve the dispute informally, you and Stackd agree to resolve any claim arising out of or related to these Terms or the Platform through binding arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules, rather than in court, except as noted below.
                </p>
                <p className="text-gray-700 font-semibold uppercase text-sm tracking-wide">
                  YOU AND STACKD WAIVE THE RIGHT TO A JURY TRIAL.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">18.3 Exceptions</h3>
                <p className="text-gray-700 leading-relaxed">
                  Either party may bring a claim in small claims court if it qualifies and remains in that court. Either party may seek injunctive or equitable relief in court to prevent unauthorized use or infringement of intellectual property or to address security breaches.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">18.4 No Class Actions</h3>
                <p className="text-gray-700 leading-relaxed">
                  You and Stackd agree that claims must be brought on an individual basis only. You may not participate in a class action, class arbitration, private attorney general action, or representative proceeding against Stackd.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">18.5 Opt-Out</h3>
                <p className="text-gray-700 leading-relaxed">
                  You may opt out of arbitration by sending a written notice signed by you within thirty (30) days of first accepting these Terms to: [Insert Opt-Out Address / Email]. If you opt out, this arbitration section will not apply, but all other Terms will remain in effect.
                </p>
              </div>
            </div>
          </section>

          {/* Section 19 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">19. Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify these Terms at any time. If we make changes, we may notify you by posting updated Terms on the Platform, by email, in-app message, or other reasonable means. Changes take effect when posted (or as otherwise stated). Your continued use of the Platform after changes become effective constitutes acceptance.
            </p>
          </section>

          {/* Section 20 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">20. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of the State of North Carolina, without regard to conflict of laws rules, except that the Federal Arbitration Act governs Section 16.
            </p>
          </section>

          {/* Section 21 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">21. Miscellaneous</h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">21.1 Entire Agreement</h3>
                <p className="text-gray-700 leading-relaxed">
                  These Terms, along with our Privacy Policy, Affiliate Disclosure, Vendor Agreement, Vendor Advertising and Lead Pricing Terms, Cookie Policy, and Financial Disclaimer (each incorporated by reference), constitute the entire agreement between you and Stackd regarding the Platform.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">21.2 Severability</h3>
                <p className="text-gray-700 leading-relaxed">
                  If any provision is found unenforceable, the remaining provisions will remain in effect.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">21.3 Assignment</h3>
                <p className="text-gray-700 leading-relaxed">
                  You may not assign these Terms without our prior written consent. We may assign these Terms as part of a merger, acquisition, or asset sale, or by operation of law.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">21.4 Contact</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Questions about these Terms can be sent to:
                </p>
                <p className="text-gray-700">
                  Email:{' '}
                  <a
                    href="mailto:info@savewithstackd.com"
                    className="font-medium underline"
                    style={{ color: '#F07A5E' }}
                  >
                    info@savewithstackd.com
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Back link */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
              style={{ color: '#F07A5E' }}
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
